import React from 'react';
import clsx from 'clsx';
import Icon from '@/shared/components/Icon';

/**
 * Canonical button primitive — matches the four button styles documented
 * in STYLE_GUIDE §5.  Inline buttons across the codebase should migrate
 * to this component when their containing file is next touched (per
 * §13).  Don't reinvent button styling in consumers.
 */

type Variant = 'primary' | 'secondary' | 'destructive' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface Props
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
    variant?: Variant;
    size?: Size;
    /** When true, shows a spinner and disables the button. */
    loading?: boolean;
    /** Optional icon rendered before the label. */
    iconLeft?: React.ReactNode;
    /** Optional icon rendered after the label. */
    iconRight?: React.ReactNode;
    /** Stretches the button to fill its container's width. */
    fullWidth?: boolean;
    children: React.ReactNode;
}

const VARIANT_CLASSES: Record<Variant, string> = {
    primary:
        'bg-mld-primary text-white shadow-sm hover:bg-mld-primary/90',
    secondary:
        'border border-mld-panel-border bg-transparent text-mld-menu-text hover:bg-mld-menu-background hover:border-mld-primary',
    destructive:
        'bg-mld-danger text-white shadow-sm hover:bg-mld-danger/90',
    ghost:
        'text-mld-text-secondary hover:bg-mld-menu-background hover:text-mld-menu-text',
};

const SIZE_CLASSES: Record<Size, string> = {
    sm: 'px-3 py-1.5 text-xs gap-1.5',
    md: 'px-4 py-2 text-sm gap-2',
    lg: 'px-6 py-2.5 text-sm gap-2',
};

const Button: React.FC<Props> = ({
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    fullWidth = false,
    disabled,
    className,
    children,
    ...rest
}) => {
    const isDisabled = disabled || loading;
    return (
        <button
            {...rest}
            disabled={isDisabled}
            className={clsx(
                'inline-flex items-center justify-center rounded-md font-medium transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-60',
                'focus:outline-none focus:ring-2 focus:ring-mld-primary focus:ring-offset-1',
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                fullWidth && 'w-full',
                className
            )}>
            {loading ? <Icon name="spinner" spin className="h-4 w-4" /> : iconLeft}
            {children}
            {!loading && iconRight}
        </button>
    );
};

export default Button;
